import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, ArrowLeft, Search, 
  Package, DollarSign, MessageSquare, Image, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Branding', 'Web Design', 'Wrap Design', 'Digital Marketing', 'Events', 'Apparel', 'Promotional', 'Print'];
const PRODUCT_TYPES = [
  { value: 'service', label: 'Service (Quote)' },
  { value: 'product', label: 'Product (Fixed Price)' }
];

const AdminProducts = () => {
  const { user, isAdmin, canAccessTool } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    images: [],
    price: 0,
    priceType: 'quote',
    categories: [],
    productType: 'service',
    variants: { sizes: [], colors: [] },
    status: 'active',
    featured: false,
    displayOrder: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin && !canAccessTool('product-manager')) {
      navigate('/dashboard');
      return;
    }
    fetchProducts();
  }, [user, isAdmin]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/all`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Image upload handler
  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    for (const file of acceptedFiles) {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      try {
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, response.data.url]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: "Upload Failed",
          description: "Failed to upload image",
          variant: "destructive"
        });
      }
    }
    
    setUploading(false);
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true
  });

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct._id}`, formData);
        toast({
          title: "Success",
          description: "Product updated successfully"
        });
      } else {
        await axios.post(`${API}/products`, formData);
        toast({
          title: "Success",
          description: "Product created successfully"
        });
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to save product",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      images: product.images || [],
      price: product.price || 0,
      priceType: product.priceType || 'quote',
      categories: product.categories || [],
      productType: product.productType || 'service',
      variants: product.variants || { sizes: [], colors: [] },
      status: product.status || 'active',
      featured: product.featured || false,
      displayOrder: product.displayOrder || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${id}`);
      toast({
        title: "Success",
        description: "Product deleted successfully"
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      images: [],
      price: 0,
      priceType: 'quote',
      categories: [],
      productType: 'service',
      variants: { sizes: [], colors: [] },
      status: 'active',
      featured: false,
      displayOrder: 0
    });
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const stats = {
    total: products.length,
    services: products.filter(p => p.productType === 'service').length,
    fixedPrice: products.filter(p => p.priceType === 'fixed').length,
    featured: products.filter(p => p.featured).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
            >
              <ArrowLeft size={18} className="mr-2" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Product Manager</h1>
              <p className="text-gray-400">Manage your products and services</p>
            </div>
          </div>
          
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
          >
            <Plus className="mr-2" size={18} />
            New Product
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 text-center">
              <Package className="mx-auto text-[#F4B400] mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-400">Total Products</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/20 border-blue-500/40">
            <CardContent className="p-4 text-center">
              <MessageSquare className="mx-auto text-blue-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.services}</p>
              <p className="text-xs text-gray-400">Services (Quote)</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/20 border-green-500/40">
            <CardContent className="p-4 text-center">
              <DollarSign className="mx-auto text-green-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.fixedPrice}</p>
              <p className="text-xs text-gray-400">Fixed Price</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/20 border-yellow-500/40">
            <CardContent className="p-4 text-center">
              <Star className="mx-auto text-yellow-500 mb-2" size={24} />
              <p className="text-2xl font-bold">{stats.featured}</p>
              <p className="text-xs text-gray-400">Featured</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white"
          />
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Package className="mx-auto text-gray-500 mb-4" size={48} />
              <p className="text-gray-400 mb-4">No products found</p>
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#F4B400] text-black hover:bg-[#d49f00]"
              >
                <Plus className="mr-2" size={18} />
                Create Your First Product
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product._id} className="bg-white text-black hover:shadow-lg transition-all overflow-hidden">
                <div className="relative">
                  {product.images?.[0] ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <Image className="text-gray-400" size={48} />
                    </div>
                  )}
                  {product.featured && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500 text-black">
                      <Star size={12} className="mr-1" />
                      Featured
                    </Badge>
                  )}
                  <Badge className={`absolute top-2 right-2 ${product.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}>
                    {product.status}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.description}</p>
                  
                  <div className="flex items-center gap-2 mb-3">
                    {product.price > 0 && (
                      <Badge variant="outline" className="border-green-500 text-green-600">
                        From ${product.price}
                      </Badge>
                    )}
                    <Badge variant="outline" className={product.priceType === 'fixed' ? 'border-blue-500 text-blue-600' : 'border-orange-500 text-orange-600'}>
                      {product.priceType === 'fixed' ? 'Add to Cart' : 'Quote'}
                    </Badge>
                    <Badge variant="outline">
                      {product.productType === 'product' ? 'Product' : 'Service'}
                    </Badge>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(product)}
                      className="flex-1"
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(product._id)}
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-black">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Create New Product'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-2"
                  placeholder="Product name"
                />
              </div>
              <div>
                <Label>Product Type *</Label>
                <Select
                  value={formData.productType}
                  onValueChange={(value) => {
                    setFormData({ 
                      ...formData, 
                      productType: value,
                      priceType: value === 'product' ? 'fixed' : 'quote'
                    });
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="mt-2"
                placeholder="Product description"
              />
            </div>

            {/* Image Upload */}
            <div>
              <Label>Images</Label>
              <div className="mt-2 space-y-3">
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={img} alt="" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-[#F4B400] bg-yellow-50' : 'border-gray-300 hover:border-[#F4B400]'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Image className="mx-auto text-gray-400 mb-2" size={32} />
                  <p className="text-sm text-gray-500">
                    {uploading ? 'Uploading...' : isDragActive ? 'Drop images here' : 'Drag & drop images or click to select'}
                  </p>
                </div>
              </div>
            </div>

            {/* Price Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price Type</Label>
                <Select
                  value={formData.priceType}
                  onValueChange={(value) => setFormData({ ...formData, priceType: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">Request Quote</SelectItem>
                    <SelectItem value="fixed">Fixed Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Starting Price ($) {formData.priceType === 'quote' && <span className="text-xs text-gray-500">(optional)</span>}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="mt-2"
                  placeholder={formData.priceType === 'quote' ? 'Leave 0 if no base price' : 'Enter price'}
                />
                {formData.priceType === 'quote' && formData.price > 0 && (
                  <p className="text-xs text-green-600 mt-1">Will show "From ${formData.price}" with quote option</p>
                )}
                {formData.priceType === 'fixed' && formData.price > 0 && (
                  <p className="text-xs text-green-600 mt-1">Will show "From ${formData.price}"</p>
                )}
              </div>
            </div>

            {/* Categories */}
            <div>
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {CATEGORIES.map((cat) => (
                  <Badge
                    key={cat}
                    variant={formData.categories.includes(cat) ? 'default' : 'outline'}
                    className={`cursor-pointer ${formData.categories.includes(cat) ? 'bg-[#F4B400] text-black' : ''}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Status and Featured */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
              <Label htmlFor="featured" className="cursor-pointer">Featured Product (shows in carousel)</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-[#F4B400] text-black hover:bg-[#d49f00]">
                {editingProduct ? 'Update Product' : 'Create Product'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
